import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import { ForgotPassword, ResetPassword } from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import Tags from './pages/Tags';
import Clients from './pages/Clients';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Requests from './pages/Requests';
import Contracts from './pages/Contracts';
import Sponsors from './pages/Sponsors';
import SheikhReport from './pages/SheikhReport';
import SponsorReport from './pages/SponsorReport';
import MonthlyReport from './pages/MonthlyReport';
import ReportTemplates from './pages/ReportTemplates';
import RecurringTasks from './pages/RecurringTasks';
import Workflows from './pages/Workflows';
import Users from './pages/Users';
import Broadcast from './pages/Broadcast';
import Transcription from './pages/Transcription';
import TranscriptSearch from './pages/TranscriptSearch';
import Reports from './pages/Reports';
import Payroll from './pages/Payroll';
import Settings from './pages/Settings';
import ActivityLog from './pages/ActivityLog';
import Profile from './pages/Profile';
import ApprovalsInbox from './pages/ApprovalsInbox';
import Notifications from './pages/Notifications';
import ClientPortal from './pages/ClientPortal';
import Help from './pages/Help';
import FundersHome from './pages/financial/FundersHome';
import FunderPeriods from './pages/financial/FunderPeriods';
import ReportCover from './pages/financial/ReportCover';
import ProjectReport from './pages/financial/ProjectReport';
import ProjectEdit from './pages/financial/ProjectEdit';
import FinancialSettings from './pages/financial/FinancialSettings';
import ReportPrint from './pages/financial/ReportPrint';
import FinancialPortal from './pages/financial/FinancialPortal';
import FinancialPortalProject from './pages/financial/FinancialPortalProject';
import ProgressReportsHome from './pages/progress/ProgressReportsHome';
import ProgressReportBuilder from './pages/progress/ProgressReportBuilder';
import ProgressPortal from './pages/progress/ProgressPortal';
import BookSummaries from './pages/books/BookSummaries';
import BookSummaryDetail from './pages/books/BookSummaryDetail';
import BooksMonthlyReport from './pages/books/BooksMonthlyReport';
import HelpTour from './components/HelpTour';
import OAuthAuthorize, { OAUTH_RETURN_KEY } from './pages/OAuthAuthorize';
import { HelpProvider } from './context/HelpContext';

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="loader">جاري التحميل...</div>;
  // زوّار البوابة يعودون لصفحة دخول العملاء لا لصفحة دخول الفريق
  if (!user) return <Navigate to={location.pathname.startsWith('/portal') ? '/support' : '/login'} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role === 'client' ? '/portal' : '/'} replace />;
  return children;
}

// ربط الموصل يبدأ من claude.ai قبل الدخول، فيعود المستخدم إلى صفحة الموافقة لا إلى الرئيسية
function afterLogin(user) {
  let back = null;
  try { back = sessionStorage.getItem(OAUTH_RETURN_KEY); } catch (_) {}
  if (back && back.startsWith('/oauth/authorize')) return back;
  return user.role === 'client' ? '/portal' : '/';
}

export default function App() {
  const { user } = useAuth();

  return (
    <HelpProvider>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={afterLogin(user)} replace /> : <Login />} />
        <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
        {/* /support هو رابط دخول العملاء المعتمد، و/portal/login مرادف له */}
        <Route path="/support" element={user ? <Navigate to={user.role === 'client' ? '/portal' : '/'} replace /> : <Login variant="client" />} />
        <Route path="/portal/login" element={<Navigate to="/support" replace />} />
        <Route path="/forgot-password" element={user ? <Navigate to={user.role === 'client' ? '/portal' : '/'} replace /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/financial/:slug" element={<FinancialPortal />} />
        <Route path="/financial/:slug/projects/:projectId" element={<FinancialPortalProject />} />
        <Route path="/progress/:slug" element={<ProgressPortal />} />
        <Route path="/reports/print/:month" element={
          <PrivateRoute roles={['admin', 'supervisor']}><MonthlyReport printable /></PrivateRoute>
        } />
        <Route path="/book-summaries/report/print/:month" element={
          <PrivateRoute roles={['admin', 'supervisor']}><BooksMonthlyReport printable /></PrivateRoute>
        } />
        <Route path="/financial-reports/:funderId/:month/print" element={
          <PrivateRoute roles={['admin', 'supervisor']}><ReportPrint /></PrivateRoute>
        } />
        <Route path="/portal/*" element={
          <PrivateRoute roles={['client']}><ClientPortal /></PrivateRoute>
        } />
        <Route path="/*" element={
          <PrivateRoute roles={['admin', 'supervisor', 'team']}>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/tasks/:id" element={<TaskDetail />} />
                <Route path="/tags" element={<PrivateRoute roles={['admin', 'supervisor']}><Tags /></PrivateRoute>} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/requests/:id" element={<Requests />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/sponsors" element={<Sponsors />} />
                <Route path="/reports/sheikh/:id" element={<PrivateRoute roles={['admin', 'supervisor']}><SheikhReport /></PrivateRoute>} />
                <Route path="/reports/sponsor/:id" element={<PrivateRoute roles={['admin', 'supervisor']}><SponsorReport /></PrivateRoute>} />
                <Route path="/report-templates" element={<PrivateRoute roles={['admin', 'supervisor']}><ReportTemplates /></PrivateRoute>} />
                <Route path="/recurring-tasks" element={<RecurringTasks />} />
                <Route path="/workflows" element={<Workflows />} />
                {/* report قبل :id — RR6 يرجّح الساكن أصلاً، والترتيب يوثّق النيّة */}
                <Route path="/book-summaries" element={<BookSummaries />} />
                <Route path="/book-summaries/report" element={<PrivateRoute roles={['admin', 'supervisor']}><BooksMonthlyReport /></PrivateRoute>} />
                <Route path="/book-summaries/:id" element={<BookSummaryDetail />} />
                <Route path="/users" element={<PrivateRoute roles={['admin', 'supervisor']}><Users /></PrivateRoute>} />
                <Route path="/broadcast" element={<PrivateRoute roles={['admin', 'supervisor']}><Broadcast /></PrivateRoute>} />
                <Route path="/transcription" element={<Transcription />} />
                <Route path="/transcription/search" element={<TranscriptSearch />} />
                <Route path="/reports" element={<PrivateRoute roles={['admin', 'supervisor']}><Reports /></PrivateRoute>} />
                <Route path="/payroll" element={<PrivateRoute roles={['admin', 'supervisor']}><Payroll /></PrivateRoute>} />
                <Route path="/progress-reports" element={<PrivateRoute roles={['admin', 'supervisor']}><ProgressReportsHome /></PrivateRoute>} />
                <Route path="/progress-reports/:id" element={<PrivateRoute roles={['admin', 'supervisor']}><ProgressReportBuilder /></PrivateRoute>} />
                <Route path="/financial-reports" element={<PrivateRoute roles={['admin', 'supervisor']}><FundersHome /></PrivateRoute>} />
                <Route path="/financial-settings" element={<PrivateRoute roles={['admin', 'supervisor']}><FinancialSettings /></PrivateRoute>} />
                <Route path="/financial-reports/:funderId" element={<PrivateRoute roles={['admin', 'supervisor']}><FunderPeriods /></PrivateRoute>} />
                <Route path="/financial-reports/:funderId/:month" element={<PrivateRoute roles={['admin', 'supervisor']}><ReportCover /></PrivateRoute>} />
                <Route path="/financial-reports/:funderId/:month/projects/:projectId" element={<PrivateRoute roles={['admin', 'supervisor']}><ProjectReport /></PrivateRoute>} />
                <Route path="/financial-reports/:funderId/:month/projects/:projectId/edit" element={<PrivateRoute roles={['admin', 'supervisor']}><ProjectEdit /></PrivateRoute>} />
                <Route path="/activity-logs" element={<ActivityLog />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/approvals" element={<ApprovalsInbox />} />
                <Route path="/help" element={<Help />} />
                <Route path="/settings" element={<PrivateRoute roles={['admin']}><Settings /></PrivateRoute>} />
              </Routes>
            </Layout>
          </PrivateRoute>
        } />
      </Routes>
      {user && <HelpTour />}
    </HelpProvider>
  );
}
